import {
  Button,
  FixedAnimationIcon,
  Icon,
  Stack,
  Text,
  useToast,
} from '@pairy/pds-react';
import React, { useEffect, useRef, useState } from 'react';
import { CtaForm, FormRecord, SelectOption } from '@pairy/sheard/types';
import { useScrollEndDetection } from '@pairy/react-hooks';
import { motion } from 'framer-motion';
import { useSetAtom } from 'jotai';

import { useViewerRoute } from '../../../../../contexts/ViewerRoute';
import DesktopFormFreeBadge from '../../../../@common/FormFreeBadge/Desktop';
import usePostFormEmailAuth from '../../../../../hooks/mutation/cta/useFormEmailAuth';
import { GOOGLE_EVENT_ID } from '../../../../../constant/googleEventId';
import { isPreviewFormSubmittedAtom } from '../../../../BulkPdfPreview/index.atom';
import { FORM_VALIDATIONS } from '../../../../../constant/formValidations';
import usePostCtaFormWithInvalidation from '../../../../../hooks/mutation/cta/usePostCtaForm';
import CtaFormModalContents from '../../../CtaFormModalContents';
import useVerifyFormEmailAuth from '../../../../../hooks/mutation/cta/useVerifyFormEmailAuth';

import * as Styled from './index.styles';
import { Spinner } from './index.styles';

interface Props {
  ctaForm?: CtaForm;
  isEmailVerificationEnabled: boolean;
  isPreview: boolean;
  closeModal: () => void;
  translateKoToEn: (item: string) => string;
  userSubscriptionPlan: string;
}

const CtaFormModal = ({
  ctaForm,
  isEmailVerificationEnabled,
  isPreview,
  closeModal,
  translateKoToEn,
  userSubscriptionPlan,
}: Props) => {
  const [formFields, setFormFields] = useState<FormRecord[]>([]);
  const { historyId } = useViewerRoute();
  const scrollRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  const setPreviewFormSubmitted = useSetAtom(isPreviewFormSubmittedAtom);

  const isBlind = ctaForm?.blind === 'none';

  const { mutate: postCtaFormMutate } = usePostCtaFormWithInvalidation();
  const { mutate: sendAuthEmail } = usePostFormEmailAuth({
    onSuccess: () => {
      setIsCodeSent(true);
      toast(`${translateKoToEn('verification-code-sent')}`);
    },
    onError: () => {
      toast(`${translateKoToEn('verification-code-failed')}`);
    },
  });
  const { mutate: verifyEmail } = useVerifyFormEmailAuth({
    onSuccess: () => {
      setIsVerified(true);
      toast(`${translateKoToEn('verification-success')}`);
    },
    onError: () => {
      setIsVerified(false);
      toast(`${translateKoToEn('verification-failed')}`);
    },
  });
  const { isEndDetection, handleScroll, scrollToBottom } =
    useScrollEndDetection(scrollRef);

  const forwardWheelToContents = (e: React.WheelEvent<HTMLDivElement>) => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }

    const { deltaY } = e;
    const { scrollTop, scrollHeight, clientHeight } = scrollEl;

    const isScrollable = scrollHeight > clientHeight;
    if (!isScrollable) {
      return;
    }

    const isScrollingDown = deltaY > 0;
    const isScrollingUp = deltaY < 0;

    const isAtTop = scrollTop <= 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    const canScrollDown = isScrollingDown && !isAtBottom;
    const canScrollUp = isScrollingUp && !isAtTop;

    if (canScrollDown || canScrollUp) {
      e.preventDefault();
      e.stopPropagation();
      scrollEl.scrollTop += deltaY;
      handleScroll();
    }
  };

  const handleSubmit = () => {
    if (isLoading) {
      return;
    }

    if (typeof historyId === 'string') {
      setIsLoading(true);
      postCtaFormMutate({
        historyId: historyId,
        formRecord: formFields,
        code: verificationCode === '' ? undefined : verificationCode,
        onSuccess: () => {
          setIsLoading(false);
          closeModal();
          toast(`${translateKoToEn('form-submit')}`);
        },
        onError: () => {
          setIsLoading(false);
        },
      });
    }
  };

  const handleChangeWithId = (id: number, value: FormRecord['value']) => {
    setFormFields((prev) =>
      prev?.map((field) => {
        if (field.id === id) {
          return {
            ...field,
            value,
          };
        }
        return field;
      }),
    );
  };

  useEffect(() => {
    if (ctaForm?.fields) {
      setFormFields(
        ctaForm?.fields.map((field) => {
          if (field.type === 'email') {
            return {
              id: field.id,
              name: field.name,
              type: field.type,
              value: field.initialValue ?? null,
            };
          }
          return {
            id: field.id,
            name: field.name,
            type: field.type,
            value: null,
          };
        }),
      );
    }
  }, []);

  const buttonDisabled = ctaForm?.fields.some((field) => {
    if (field.isRequired) {
      const currentValue = formFields?.find((f) => f.id === field.id)?.value;

      if (!currentValue) {
        return true;
      }

      if (field.type === 'number') {
        const numberValue = currentValue as number;
        return !(
          numberValue <= field.range.max && numberValue >= field.range.min
        );
      }

      if (field.type === 'select') {
        const selectedValue = currentValue as SelectOption[];
        return !(
          selectedValue.length >= field.range.min &&
          selectedValue.length <= field.range.max
        );
      }

      if (field.type === 'email') {
        const emailValue = currentValue as string;

        const isInvalidEmail =
          !FORM_VALIDATIONS.email.pattern.value.test(emailValue as string) &&
          Boolean(emailValue);

        return isInvalidEmail || (isEmailVerificationEnabled && !isVerified);
      }

      if (field.type === 'string' || field.type === 'name') {
        const stringValue = currentValue as string;
        return !stringValue || stringValue.trim() === '';
      }

      return !currentValue;
    }
  });

  const handleSendCode = () => {
    if (isPreview) {
      setIsCodeSent(true);
      toast(`미리보기 모드입니다. 아무 코드나 입력하셔도 인증이 완료됩니다.`);
      return;
    }
    sendAuthEmail({
      email: formFields.find((f) => f.type === 'email')?.value as string,
      historyId: historyId as string,
    });
  };

  const handleVerify = () => {
    if (isPreview) {
      setIsVerified(true);
      toast(`${translateKoToEn('verification-success')}`);
      return;
    }

    verifyEmail({
      email: formFields.find((f) => f.type === 'email')?.value as string,
      code: verificationCode,
      historyId: historyId as string,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <Styled.Modal
        direction="column"
        gap={32}
        width="375px"
        justifyContent="center"
        onWheel={forwardWheelToContents}
      >
        <Text variant="xl-semibold" color="white" textAlign="center">
          {ctaForm?.description}
        </Text>
        <Styled.ModalBody direction="column" gap={24}>
          <Stack
            justifyContent="space-between"
            alignItems="flex-start"
            gap={16}
          >
            <Text variant="lg-semibold" color="black">
              {ctaForm?.name}
            </Text>
            {isBlind && (
              <Styled.CloseButton type="button" onClick={closeModal}>
                <Text variant="md-medium" color="zinc_400">
                  {translateKoToEn('close')}
                </Text>
              </Styled.CloseButton>
            )}
          </Stack>
          <Styled.ModalContents
            direction="column"
            gap={16}
            onScroll={handleScroll}
            ref={scrollRef}
          >
            <CtaFormModalContents
              formFields={formFields}
              handleChangeWithId={handleChangeWithId}
              translateKoToEn={translateKoToEn}
              fields={ctaForm?.fields}
              isCodeSent={isCodeSent}
              isVerified={isVerified}
              verificationCode={verificationCode}
              onSendVerificationCode={handleSendCode}
              onVerifyCode={handleVerify}
              onChangeVerificationCode={setVerificationCode}
              isEmailVerificationEnabled={isEmailVerificationEnabled}
            />
            {!isEndDetection && (
              <FixedAnimationIcon
                size={24}
                icon="ph:caret-double-down"
                bottom={userSubscriptionPlan === 'Free' ? 120 : 90}
                onClick={scrollToBottom}
              />
            )}
          </Styled.ModalContents>
          <Styled.FlexBottomCTAButtonWrapper>
            <Button
              id={GOOGLE_EVENT_ID.form}
              variant="black_fill"
              radius={9999}
              paddingY={12}
              textVariant="lg-semibold"
              disabled={buttonDisabled || isLoading}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();

                if (isPreview) {
                  setPreviewFormSubmitted(true);
                  closeModal();
                  return;
                }

                handleSubmit();
              }}
            >
              {isLoading && (
                <>
                  <Spinner>
                    <Icon icon="ph:circle-notch" size={20} color="zinc_400" />
                  </Spinner>{' '}
                </>
              )}
              {ctaForm?.button}
            </Button>
            <DesktopFormFreeBadge userSubscriptionPlan={userSubscriptionPlan} />
          </Styled.FlexBottomCTAButtonWrapper>
        </Styled.ModalBody>
      </Styled.Modal>
      <Styled.Dim />
    </motion.div>
  );
};

export default CtaFormModal;
